import { useMutation, useQuery } from "@apollo/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, Box, IconButton, Snackbar, Tooltip } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import type { ExpanderComponentProps, SortOrder, TableColumn } from "react-data-table-component";

import { Button } from "@/components/atoms/Button/styles";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import {
  CallPlanInfoFragment,
  DeactivateCallPlanMutation,
  DeactivateCallPlanMutationVariables,
  GetTreatmentHistoryQuery,
  GetTreatmentHistoryQueryVariables,
  PlaylistDetailedFragment,
  ReactivateCallPlanMutation,
  ReactivateCallPlanMutationVariables,
} from "@/graphql/graphql";
import { PlaylistTableWrapper, RightHeaderDiv, RightStyledH1 } from "@/pages/UserDetails/styles";
import { fontFamilyDefault } from "@/styles/typography";
import { formatGqlExecutionErrors, graphqlClientErrorMessage } from "@/utils/format";

import { StatusChip } from "../Playlists/components/StatusChip";
import { GET_TREATMENT_HISTORY } from "../UserInfo/components/TreatmentHistoryTable/queries";
import {
  buildCallPlanColumns,
  buildCallPlanTableRows,
  CALL_PLAN_TABLE_COLUMN_IDS,
  CallPlanTableRow,
  deactivateCallPlanConfirmDialogContent,
  duplicateProgramWeekEarlierPlanIds,
  formatCallPlanDateRange,
  getCallPlanDeleteState,
  getCallPlanEditState,
  reactivateCallPlanConfirmDialogContent,
  sortSegmentPlansForTable,
} from "./CallPlans.columns";
import { CALL_PLAN_SNACKBAR_AUTO_HIDE_MS, callPlanSnackbarOnClose } from "./CallPlans.snackbar";
import CreateCallPlanModal from "./CreateCallPlanModal";
import {
  CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE,
  isActiveProgram,
} from "./CreateCallPlanModal/CreateCallPlanModal.utils";
import { PatientTimezoneDateBlurb } from "./PatientTimezoneDateBlurb";
import ViewCallPlanModal from "./ViewCallPlanModal/ViewCallPlanModal";
import {
  DEACTIVATE_CALL_PLAN_MUTATION,
  REACTIVATE_CALL_PLAN_MUTATION,
} from "./ViewCallPlanModal/ViewCallPlanModal.mutations";

type CallPlansProps = {
  accountId: number;
  accountTimeZone?: string | null;
  callPlans: CallPlanInfoFragment[];
  playlists?: PlaylistDetailedFragment[];
};

type CallPlansTableSortState = {
  columnId: string | number | undefined;
  direction: "asc" | "desc";
};

type CallPlanTableExpanderProps = ExpanderComponentProps<CallPlanTableRow> & {
  sortDirection: "asc" | "desc";
  accountTimeZone?: string | null;
  onViewPlan: (plan: CallPlanInfoFragment) => void;
};

const callPlansExpandPlaceholderCellSx = {
  width: 48,
  minHeight: "40px",
  cursor: "pointer",
} as const;

const groupedExpandedRootSx = {
  fontFamily: fontFamilyDefault,
  backgroundColor: "#FEFAF1",
  color: "rgba(0, 0, 0, 0.87)",
  borderTop: "1px solid rgba(0, 0, 0, 0.12)",
};

/** Matches react-data-table-component: expander + equal-grow data cols + fixed actions slot (RDT Cell flex-grow). */
const groupedExpandedSegmentRowSx = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box" as const,
  minHeight: "48px",
  fontSize: 12,
  lineHeight: "22px",
  fontWeight: 400,
  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  "&:last-of-type": {
    borderBottom: "1px solid rgba(0, 0, 0, 0.22)",
  },
  "&:hover": {
    backgroundColor: "#fff6e3",
    boxShadow: "5px 0px 5px rgba(0,0,0,0.2)",
  },
  cursor: "pointer",
};

const groupedExpandedExpanderSlotSx = {
  flex: "0 0 48px",
  width: 48,
  flexShrink: 0,
};

const groupedExpandedDataCellSx = {
  flex: "1 1 0%",
  minWidth: 0,
  px: 2,
  py: "5px",
  wordBreak: "break-word" as const,
};

const groupedExpandedActionsSlotSx = {
  flex: "0 0 88px",
  width: 88,
  flexShrink: 0,
};

const expandToggleChevronIconSxBase = {
  fontSize: "1.35rem",
  transition: "transform 150ms ease",
} as const;

const expandToggleChevronIconSxExpanded = {
  ...expandToggleChevronIconSxBase,
  transform: "rotate(180deg)",
} as const;

const CallPlanTableExpander: React.FC<CallPlanTableExpanderProps> = ({
  data,
  sortDirection,
  accountTimeZone,
  onViewPlan,
}) => {
  const sortedPlans = useMemo(() => {
    if (!data?.segmentPlans?.length) {
      return [];
    }
    return sortSegmentPlansForTable(data.segmentPlans, sortDirection);
  }, [data?.segmentPlans, sortDirection]);

  if (!sortedPlans.length) {
    return null;
  }

  return (
    <Box sx={groupedExpandedRootSx}>
      {sortedPlans.map((plan) => (
        <Box
          key={plan.id}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onViewPlan(plan);
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            onViewPlan(plan);
          }}
          sx={groupedExpandedSegmentRowSx}
        >
          <Box aria-hidden sx={groupedExpandedExpanderSlotSx} />
          <Box sx={groupedExpandedDataCellSx}>{plan.program?.round ?? "-"}</Box>
          <Box sx={groupedExpandedDataCellSx}>{plan.programWeek}</Box>
          <Box sx={groupedExpandedDataCellSx}>{formatCallPlanDateRange(plan, accountTimeZone)}</Box>
          <Box sx={groupedExpandedDataCellSx}>{plan.activityCount}</Box>
          <Box sx={groupedExpandedDataCellSx}>
            <StatusChip isActive={plan.isActive} />
          </Box>
          <Box aria-hidden sx={groupedExpandedActionsSlotSx} />
        </Box>
      ))}
    </Box>
  );
};

const CallPlans: React.FC<CallPlansProps> = ({
  accountId,
  accountTimeZone,
  callPlans,
  playlists = [],
}) => {
  const [createCallPlanModalOpen, setCreateCallPlanModalOpen] = useState(false);
  const [callPlanToEdit, setCallPlanToEdit] = useState<CallPlanInfoFragment | null>(null);
  const [callPlanToView, setCallPlanToView] = useState<CallPlanInfoFragment | undefined>();
  const [expandedRowKeys, setExpandedRowKeys] = useState<Record<string, boolean>>({});
  const [callPlanToDeactivate, setCallPlanToDeactivate] = useState<CallPlanInfoFragment | null>(
    null
  );
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deactivateErrorOpen, setDeactivateErrorOpen] = useState(false);
  const [deactivateErrorMessage, setDeactivateErrorMessage] = useState<string | null>(null);
  const [callPlanToReactivate, setCallPlanToReactivate] = useState<CallPlanInfoFragment | null>(
    null
  );
  const [reactivateConfirmOpen, setReactivateConfirmOpen] = useState(false);
  const [reactivateErrorOpen, setReactivateErrorOpen] = useState(false);
  const [reactivateErrorMessage, setReactivateErrorMessage] = useState<string | null>(null);
  const [tableSort, setTableSort] = useState<CallPlansTableSortState>({
    columnId: CALL_PLAN_TABLE_COLUMN_IDS.DATE_RANGE,
    direction: "desc",
  });
  const { data: programsData, loading: programsLoading } = useQuery<
    GetTreatmentHistoryQuery,
    GetTreatmentHistoryQueryVariables
  >(GET_TREATMENT_HISTORY, {
    variables: { accountId },
    fetchPolicy: "network-only",
  });

  const [deactivateCallPlan] = useMutation<
    DeactivateCallPlanMutation,
    DeactivateCallPlanMutationVariables
  >(DEACTIVATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });

  const [reactivateCallPlan] = useMutation<
    ReactivateCallPlanMutation,
    ReactivateCallPlanMutationVariables
  >(REACTIVATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });

  const closeCreateCallPlanModal = () => {
    setCreateCallPlanModalOpen(false);
    setCallPlanToEdit(null);
  };

  const closeViewCallPlanModal = () => {
    setCallPlanToView(undefined);
  };

  const openEditFromView = (callPlan: CallPlanInfoFragment) => {
    setCallPlanToView(undefined);
    setCallPlanToEdit(callPlan);
    setCreateCallPlanModalOpen(true);
  };

  const requestDeactivateCallPlan = useCallback((plan: CallPlanInfoFragment) => {
    if (!plan.isActive) {
      return;
    }
    setCallPlanToDeactivate(plan);
    setDeactivateConfirmOpen(true);
  }, []);

  const closeDeactivateConfirm = useCallback(() => {
    setDeactivateConfirmOpen(false);
    setCallPlanToDeactivate(null);
  }, []);

  const handleConfirmDeactivateCallPlan = useCallback(async () => {
    if (!callPlanToDeactivate?.isActive) {
      return;
    }
    setDeactivateConfirmOpen(false);
    const planId = callPlanToDeactivate.id;
    setCallPlanToDeactivate(null);
    try {
      const result = await deactivateCallPlan({ variables: { id: planId } });
      if (result.data?.deactivateCallPlan !== true) {
        setDeactivateErrorMessage(
          formatGqlExecutionErrors(result) ?? "Could not deactivate the call plan."
        );
        setDeactivateErrorOpen(true);
        return;
      }
      if (callPlanToView?.id === planId) {
        setCallPlanToView(undefined);
      }
    } catch (e) {
      setDeactivateErrorMessage(
        graphqlClientErrorMessage(e, "Could not deactivate the call plan.")
      );
      setDeactivateErrorOpen(true);
    }
  }, [callPlanToDeactivate, callPlanToView?.id, deactivateCallPlan]);

  const requestReactivateCallPlan = useCallback((plan: CallPlanInfoFragment) => {
    setCallPlanToReactivate(plan);
    setReactivateConfirmOpen(true);
  }, []);

  const closeReactivateConfirm = useCallback(() => {
    setReactivateConfirmOpen(false);
    setCallPlanToReactivate(null);
  }, []);

  const handleConfirmReactivateCallPlan = useCallback(async () => {
    if (!callPlanToReactivate) {
      return;
    }
    setReactivateConfirmOpen(false);
    const planId = callPlanToReactivate.id;
    setCallPlanToReactivate(null);
    try {
      const result = await reactivateCallPlan({ variables: { id: planId } });
      if (result.data?.reactivateCallPlan !== true) {
        setReactivateErrorMessage(
          formatGqlExecutionErrors(result) ?? "Could not reactivate the call plan."
        );
        setReactivateErrorOpen(true);
        return;
      }
      if (callPlanToView?.id === planId) {
        setCallPlanToView(undefined);
      }
    } catch (e) {
      setReactivateErrorMessage(
        graphqlClientErrorMessage(e, "Could not reactivate the call plan.")
      );
      setReactivateErrorOpen(true);
    }
  }, [callPlanToReactivate, callPlanToView?.id, reactivateCallPlan]);

  const duplicateWeekEarlierPlanIds = useMemo(
    () => duplicateProgramWeekEarlierPlanIds(callPlans),
    [callPlans]
  );

  const tableRows = useMemo(() => buildCallPlanTableRows(callPlans), [callPlans]);

  const handleTableSort = useCallback(
    (selectedColumn: TableColumn<CallPlanTableRow>, sortDirection: SortOrder) => {
      setTableSort({
        columnId: selectedColumn.id ?? CALL_PLAN_TABLE_COLUMN_IDS.DATE_RANGE,
        direction: sortDirection === "asc" ? "asc" : "desc",
      });
    },
    []
  );

  const activeProgram = useMemo(() => {
    if (!programsLoading && programsData) {
      return programsData.treatmentHistory.find(isActiveProgram);
    }
  }, [programsLoading, programsData]);

  const tableColumns = useMemo((): TableColumn<CallPlanTableRow>[] => {
    const expandToggleColumn: TableColumn<CallPlanTableRow> = {
      id: "_callPlansExpandToggle",
      name: "",
      width: "48px",
      minWidth: "48px",
      maxWidth: "48px",
      grow: 0,
      button: true,
      ignoreRowClick: true,
      cell: (row) =>
        row.segmentPlans.length >= 2 ? (
          <IconButton
            size="small"
            aria-label={
              expandedRowKeys[row.rowKey]
                ? "Collapse grouped call plans"
                : "Expand grouped call plans"
            }
            aria-expanded={Boolean(expandedRowKeys[row.rowKey])}
            onClick={(e) => {
              e.stopPropagation();
              setExpandedRowKeys((prev) => ({
                ...prev,
                [row.rowKey]: !prev[row.rowKey],
              }));
            }}
          >
            <ExpandMoreIcon
              sx={
                expandedRowKeys[row.rowKey]
                  ? expandToggleChevronIconSxExpanded
                  : expandToggleChevronIconSxBase
              }
            />
          </IconButton>
        ) : (
          <Box
            role="button"
            tabIndex={0}
            aria-label="View call plan"
            sx={callPlansExpandPlaceholderCellSx}
            onClick={(e) => {
              e.stopPropagation();
              setCallPlanToView(row.segmentPlans[0]);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setCallPlanToView(row.segmentPlans[0]);
              }
            }}
          />
        ),
    };

    return [
      expandToggleColumn,
      ...buildCallPlanColumns(
        (row) => {
          setCallPlanToEdit(row);
          setCreateCallPlanModalOpen(true);
        },
        requestDeactivateCallPlan,
        requestReactivateCallPlan,
        callPlans,
        duplicateWeekEarlierPlanIds,
        !activeProgram,
        accountTimeZone
      ),
    ];
  }, [
    accountTimeZone,
    activeProgram,
    callPlans,
    duplicateWeekEarlierPlanIds,
    expandedRowKeys,
    requestDeactivateCallPlan,
    requestReactivateCallPlan,
    setCallPlanToView,
  ]);

  const viewModalEditState = useMemo(
    () =>
      callPlanToView &&
      getCallPlanEditState(callPlanToView, duplicateWeekEarlierPlanIds, !activeProgram),
    [activeProgram, callPlanToView, duplicateWeekEarlierPlanIds]
  );

  const viewModalDeleteState = useMemo(
    () => callPlanToView && getCallPlanDeleteState(callPlanToView, duplicateWeekEarlierPlanIds),
    [callPlanToView, duplicateWeekEarlierPlanIds]
  );

  const expandableRowProps = useMemo(
    () => ({
      sortDirection: tableSort.direction,
      accountTimeZone,
      onViewPlan: setCallPlanToView,
      onDeactivateCallPlan: requestDeactivateCallPlan,
    }),
    [accountTimeZone, requestDeactivateCallPlan, tableSort.direction]
  );

  return (
    <>
      <RightHeaderDiv>
        <RightStyledH1>
          Call Plans
          <Tooltip title={!activeProgram ? CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE : ""}>
            <span>
              <Button
                disabled={!activeProgram}
                onClick={() => {
                  setCallPlanToEdit(null);
                  setCreateCallPlanModalOpen(true);
                }}
              >
                Create New Call Plan
              </Button>
            </span>
          </Tooltip>
        </RightStyledH1>
      </RightHeaderDiv>
      <PlaylistTableWrapper>
        <PatientTimezoneDateBlurb accountTimeZone={accountTimeZone} />
        <StyledDataTable
          keyField="rowKey"
          columns={tableColumns}
          data={tableRows}
          defaultSortFieldId={CALL_PLAN_TABLE_COLUMN_IDS.DATE_RANGE}
          defaultSortAsc={false}
          onSort={handleTableSort}
          expandableRows
          expandableRowsHideExpander
          expandableRowExpanded={(row: CallPlanTableRow) => Boolean(expandedRowKeys[row.rowKey])}
          expandableRowsComponent={
            CallPlanTableExpander as React.ComponentType<ExpanderComponentProps<CallPlanTableRow>>
          }
          expandableRowsComponentProps={expandableRowProps}
          onRowClicked={(row: CallPlanTableRow) => {
            if (row.segmentPlans.length > 1) {
              setExpandedRowKeys((prev) => ({
                ...prev,
                [row.rowKey]: !prev[row.rowKey],
              }));
              return;
            }
            setCallPlanToView(row.segmentPlans[0]);
          }}
        />
      </PlaylistTableWrapper>

      {callPlanToView && (
        <ViewCallPlanModal
          open={Boolean(callPlanToView)}
          accountTimeZone={accountTimeZone}
          callPlans={callPlans}
          callPlanToView={callPlanToView}
          editState={viewModalEditState}
          deleteState={viewModalDeleteState}
          onClose={closeViewCallPlanModal}
          onRequestEdit={openEditFromView}
        />
      )}

      {createCallPlanModalOpen && (
        <CreateCallPlanModal
          accountId={accountId}
          accountTimeZone={accountTimeZone}
          callPlans={callPlans}
          playlists={playlists}
          activeProgram={activeProgram}
          callPlanToEdit={callPlanToEdit}
          onCallPlanCreated={(plan) => {
            setCallPlanToView(plan);
          }}
          open={createCallPlanModalOpen}
          onClose={closeCreateCallPlanModal}
        />
      )}

      {deactivateConfirmOpen && (
        <ConfirmDialog
          confirmOpen={deactivateConfirmOpen}
          dialogContent={deactivateCallPlanConfirmDialogContent}
          onClose={closeDeactivateConfirm}
          onSubmit={handleConfirmDeactivateCallPlan}
        />
      )}

      {reactivateConfirmOpen && (
        <ConfirmDialog
          confirmOpen={reactivateConfirmOpen}
          dialogContent={reactivateCallPlanConfirmDialogContent}
          onClose={closeReactivateConfirm}
          onSubmit={handleConfirmReactivateCallPlan}
        />
      )}

      {deactivateErrorOpen && (
        <Snackbar
          open
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={callPlanSnackbarOnClose(() => setDeactivateErrorOpen(false))}
        >
          <Alert
            onClose={() => setDeactivateErrorOpen(false)}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {deactivateErrorMessage ?? "Could not deactivate the call plan."}
          </Alert>
        </Snackbar>
      )}

      {reactivateErrorOpen && (
        <Snackbar
          open
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={callPlanSnackbarOnClose(() => setReactivateErrorOpen(false))}
        >
          <Alert
            onClose={() => setReactivateErrorOpen(false)}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {reactivateErrorMessage ?? "Could not reactivate the call plan."}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default CallPlans;
